import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Loader2, Info } from "lucide-react";
import api from "@/lib/api";
import SmoothScroll from "@/components/site/SmoothScroll";
import Header from "@/components/site/Header";
import TopMarquee from "@/components/site/TopMarquee";
import Footer from "@/components/site/Footer";
import { ShareRow } from "@/components/site/Share";
import RichBody from "@/components/site/RichBody";
import Thumb from "@/components/site/Thumb";
import PopupAd from "@/components/site/PopupAd";
import CategoryNav from "@/components/site/CategoryNav";
import { useLang } from "@/context/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// --- SEO helpers ---
function setMeta(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setMetaProp(prop, content) {
  if (!content) return;
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, isEn } = useLang();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setA(null); setNotFound(false);
    // Article body is always served in Hindi (the language it was written in).
    // The UI EN toggle only switches menu/UI labels; the article shows the
    // "originally in Hindi" note when isEn is true.
    api.get(`/public/articles/${slug}`)
      .then((r) => {
        setA(r.data);
        document.title = `${r.data.title} | ${isEn ? "Denva Post" : "देनवा पोस्ट"}`;
        // Meta description
        setMeta("description", r.data.summary || r.data.title);
        // Canonical link
        setLink("canonical", `${window.location.origin}/news/${r.data.slug}`);
        // Open Graph + Twitter (for direct SPA URL scrapes)
        setMetaProp("og:type", "article");
        setMetaProp("og:title", r.data.title);
        setMetaProp("og:description", r.data.summary || "");
        setMetaProp("og:image", r.data.img || "");
        setMetaProp("og:url", `${window.location.origin}/news/${r.data.slug}`);
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", r.data.title);
        setMeta("twitter:image", r.data.img || "");
      })
      .catch(() => setNotFound(true));
  }, [slug, isEn]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] flex flex-col items-center justify-center font-sans-dev px-4 text-center">
        <div className="font-serif-dev text-5xl font-black mb-4">404</div>
        <p className="text-[#4A4A4A] mb-6">{t("not_found")}</p>
        <Link to="/" className="pill-btn text-[12px]">{t("go_home")}</Link>
      </div>
    );
  }
  if (!a) {
    return <div className="min-h-screen bg-[#F4F4F0] flex items-center justify-center"><Loader2 className="animate-spin text-[#D92525]" /></div>;
  }

  const shareUrl = `${BACKEND}/api/share/${a.slug}`;
  const bodyText = a.body || a.summary || "";
  const authorHref = a.author?.id ? `/author/${a.author.id}` : null;
  const articleUrl = typeof window !== "undefined" ? `${window.location.origin}/news/${a.slug}` : `/news/${a.slug}`;
  const publishedISO = a.published_at || a.created_at || new Date().toISOString();

  const newsSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "mainEntityOfPage": { "@type": "WebPage", "@id": articleUrl },
    "headline": a.title,
    "description": a.summary || a.title,
    "image": [a.img].filter(Boolean),
    "datePublished": publishedISO,
    "dateModified": publishedISO,
    "articleSection": a.kicker || "News",
    "inLanguage": "hi",
    "author": {
      "@type": "Person",
      "name": a.author?.name || "देनवा पोस्ट संपादकीय",
      ...(authorHref ? { "url": `${typeof window !== "undefined" ? window.location.origin : ""}${authorHref}` } : {}),
    },
    "publisher": {
      "@type": "NewsMediaOrganization",
      "name": "Denva Post · देनवा पोस्ट",
      "logo": { "@type": "ImageObject", "url": "https://customer-assets-eiarnc6j.emergentagent.net/job_content-pipeline-62/artifacts/je9u0dms_News%20Logo.png" },
    },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "होम", "item": typeof window !== "undefined" ? window.location.origin : "/" },
      { "@type": "ListItem", "position": 2, "name": a.kicker || "News" },
      { "@type": "ListItem", "position": 3, "name": a.title },
    ],
  };

  return (
    <SmoothScroll>
      <div className="App font-sans-dev" data-testid="article-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(newsSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        <TopMarquee />
        <Header />
        <CategoryNav current={a.category || ""} />
        <article className="max-w-[820px] mx-auto px-4 md:px-8 py-10 md:py-16">
          <Link to="/" data-testid="article-back" className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#D92525] mb-6"><ArrowLeft size={16} /> {t("all_news")}</Link>

          <div className="eyebrow text-[#D92525] mb-4">{a.kicker} · {a.time}</div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-serif-dev font-black text-[30px] sm:text-[40px] md:text-[48px] leading-[1.1] text-[#0F0F0F]">
            {a.title}
          </motion.h1>
          {a.summary && <p className="mt-5 font-serif-dev italic text-[18px] md:text-[21px] leading-relaxed text-[#4A4A4A]">{a.summary}</p>}

          {isEn && (
            <div data-testid="article-en-note" className="mt-5 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-[13px] rounded-lg px-3.5 py-2.5">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>{t("published_in_hindi_note")}</span>
            </div>
          )}

          {/* byline */}
          <div className="mt-6 flex items-center justify-between flex-wrap gap-4 border-y border-[#0F0F0F]/15 py-4">
            <div className="flex items-center gap-3">
              <img src={a.author?.photo} alt={a.author?.name} className="w-11 h-11 rounded-full object-cover bg-gray-100 border border-[#0F0F0F]/10" />
              <div>
                {authorHref ? (
                  <Link
                    to={authorHref}
                    data-testid="article-author-link"
                    className="font-serif-dev font-bold text-[15px] hover:text-[#D92525] transition-colors"
                  >
                    {a.author?.name}
                  </Link>
                ) : (
                  <div className="font-serif-dev font-bold text-[15px]">{a.author?.name}</div>
                )}
                <div className="text-[11px] uppercase tracking-[0.16em] text-[#4A4A4A] flex items-center gap-2">
                  {a.author?.designation || t("news_desk")} <span className="opacity-40">·</span> <Clock size={11} /> {a.time}
                </div>
              </div>
            </div>
            <div data-testid="article-share-top">
              <ShareRow title={a.title} url={shareUrl} size="lg" testid="article-share-top" />
            </div>
          </div>

          {/* hero image */}
          <div className="mt-8 thumb-wrap border border-[#0F0F0F] aspect-[16/9] bg-black">
            <Thumb src={a.img} alt={a.title} fit="contain" loading="eager" />
          </div>

          {/* body */}
          <div className="mt-8 space-y-5 font-serif-dev text-[17px] md:text-[19px] leading-[1.8] text-[#1a1a1a]">
            <RichBody text={bodyText} />
          </div>

          {/* bottom share */}
          <div className="mt-12 pt-6 border-t border-[#0F0F0F]/15 flex items-center justify-between flex-wrap gap-4">
            <span className="font-sans-dev text-[12px] uppercase tracking-[0.2em] text-[#4A4A4A]">{t("share_this")}</span>
            <div data-testid="article-share-bottom">
              <ShareRow title={a.title} url={shareUrl} size="lg" testid="article-share-bottom" />
            </div>
          </div>
        </article>
        <PopupAd />
        <Footer />
      </div>
    </SmoothScroll>
  );
}
